import React, { useState } from 'react';
import {
  Card,
  Typography,
  Upload,
  Button,
  Space,
  Alert,
  message,
  Tag,
  Divider,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { controlloUdcAPI } from '../services/api.ts';

const { Title, Text, Paragraph } = Typography;

interface FileValidation {
  filename: string;
  valid: boolean;
  errors: string[];
}

interface ValidateResult {
  valid: boolean;
  files: {
    A: FileValidation;
    C: FileValidation;
  };
}

interface ProcessStats {
  a_sheet: string;
  c_sheet: string;
  a_rows: number;
  c_rows: number;
  a_udc_total: number;
  c_cod_total: number;
  a_udc_only: number;
  c_cod_only: number;
  a_udc_only_distinct: number;
  c_cod_only_distinct: number;
  a_only_list: string[];
  c_only_list: string[];
  common: number;
}

type Slot = 'A' | 'C';

const SLOT_LABELS: Record<Slot, string> = {
  A: 'A - File Aziendale (foglio TEBRO)',
  C: 'C - File Magazzino Remoto (foglio Giacenze)',
};

const ACCEPT = '.xls,.xlsx,.ods';

// Il detail del backend può essere una stringa (HTTPException) o una lista di
// errori di validazione FastAPI (422): in entrambi i casi un testo leggibile.
const errorText = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ');
  }
  if (error?.code === 'ECONNABORTED') return 'Tempo di attesa scaduto: il file è troppo grande o il server è occupato.';
  return fallback;
};

const ControlloUDC: React.FC = () => {
  const [files, setFiles] = useState<Record<Slot, File | null>>({ A: null, C: null });
  const [fileLists, setFileLists] = useState<Record<Slot, UploadFile[]>>({ A: [], C: [] });
  const [validating, setValidating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [validation, setValidation] = useState<ValidateResult | null>(null);
  const [result, setResult] = useState<ProcessStats | null>(null);

  const allFilesSelected = !!files.A && !!files.C;
  const canProcess = !!validation?.valid;

  const resetValidation = () => {
    setValidation(null);
    setResult(null);
  };

  const handleSelect = (slot: Slot, file: File) => {
    setFiles((prev) => ({ ...prev, [slot]: file }));
    setFileLists((prev) => ({
      ...prev,
      [slot]: [{ uid: slot, name: file.name, status: 'done' } as UploadFile],
    }));
    resetValidation();
  };

  const handleRemove = (slot: Slot) => {
    setFiles((prev) => ({ ...prev, [slot]: null }));
    setFileLists((prev) => ({ ...prev, [slot]: [] }));
    resetValidation();
  };

  const handleValidate = async () => {
    if (!files.A || !files.C) return;
    setValidating(true);
    resetValidation();
    try {
      const data: ValidateResult = await controlloUdcAPI.validate(files.A, files.C);
      setValidation(data);
      if (data.valid) {
        message.success('Validazione superata: puoi avviare il controllo dei dati.');
      } else {
        message.warning('Validazione non superata: correggi i file segnalati.');
      }
    } catch (error: any) {
      message.error(errorText(error, 'Errore durante la validazione dei file'));
    } finally {
      setValidating(false);
    }
  };

  const handleProcess = async () => {
    if (!files.A || !files.C) return;
    setProcessing(true);
    setResult(null);
    try {
      const { stats, filenames } = await controlloUdcAPI.process(files.A, files.C);
      message.success(`Controllo completato. File scaricati: ${filenames.join(', ')}`);
      setResult(stats as ProcessStats);
    } catch (error: any) {
      message.error(errorText(error, 'Errore durante il controllo dei dati'));
    } finally {
      setProcessing(false);
    }
  };

  const renderUpload = (slot: Slot) => (
    <div>
      <Text strong>{SLOT_LABELS[slot]}</Text>
      <div style={{ marginTop: 4 }}>
        <Upload
          accept={ACCEPT}
          maxCount={1}
          fileList={fileLists[slot]}
          beforeUpload={(f) => {
            handleSelect(slot, f as unknown as File);
            return false;
          }}
          onRemove={() => handleRemove(slot)}
        >
          <Button icon={<UploadOutlined />}>Seleziona file (.xls / .xlsx / .ods)</Button>
        </Upload>
      </div>
    </div>
  );

  const renderFileResult = (slot: Slot, info?: FileValidation) => {
    if (!info) return null;
    return (
      <div style={{ marginBottom: 8 }}>
        <Space>
          {info.valid ? <Tag color="green">{slot} OK</Tag> : <Tag color="red">{slot} ERRORE</Tag>}
          <Text type="secondary">{info.filename}</Text>
        </Space>
        {info.errors.length > 0 && (
          <ul style={{ margin: '4px 0 0 0', color: '#cf1322' }}>
            {info.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div>
      <Title level={3}>
        <FileExcelOutlined /> Controllo UDC
      </Title>
      <Paragraph type="secondary">
        Confronta la presenza dei colli tra <strong>A - File Aziendale</strong> (foglio <Text code>TEBRO</Text>,
        colonna <Text code>UDC COLLO</Text>) e <strong>C - File Magazzino Remoto</strong>
        (foglio <Text code>Giacenze</Text>, colonna <Text code>Codice</Text>). Il confronto è a insiemi
        (trim + maiuscolo; le formule valgono per il loro risultato e i codici numerici sono confrontati come
        numeri interi). Dopo la validazione, il controllo produce <strong>due file</strong> (copia di A e copia di C,
        con timestamp), scaricati separatamente:
        <Tag color="orange" style={{ marginLeft: 6 }}>arancione</Tag> su <Text code>UDC COLLO</Text> di A presenti
        in A ma non in C, e <Tag color="orange">arancione</Tag> su <Text code>Codice</Text> di C presenti in C ma non in A.
      </Paragraph>

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {renderUpload('A')}
          {renderUpload('C')}

          <Space>
            <Button
              type="default"
              icon={<CheckCircleOutlined />}
              onClick={handleValidate}
              loading={validating}
              disabled={!allFilesSelected || processing}
            >
              Valida file
            </Button>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleProcess}
              loading={processing}
              disabled={!canProcess || validating}
            >
              Avvia controllo dati
            </Button>
          </Space>
        </Space>
      </Card>

      {validation && (
        <Card style={{ marginTop: 16 }}>
          <Alert
            type={validation.valid ? 'success' : 'error'}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              validation.valid
                ? 'Tutti i file sono validi. Puoi avviare il controllo dei dati.'
                : 'Sono presenti errori di validazione: correggi i file indicati.'
            }
          />
          <Divider orientation="left">Dettaglio validazione</Divider>
          {renderFileResult('A', validation.files.A)}
          {renderFileResult('C', validation.files.C)}
        </Card>
      )}

      {result && (
        <Card style={{ marginTop: 16 }} title="Esito del controllo">
          <Row gutter={16}>
            <Col xs={12} md={4}><Statistic title="UDC COLLO in A (distinti)" value={result.a_udc_total} /></Col>
            <Col xs={12} md={4}><Statistic title="Codici in C (distinti)" value={result.c_cod_total} /></Col>
            <Col xs={12} md={4}><Statistic title="In comune" value={result.common} valueStyle={{ color: '#389e0d' }} /></Col>
            <Col xs={12} md={4}>
              <Statistic
                title="Solo in A (righe / distinti)"
                value={`${result.a_udc_only} / ${result.a_udc_only_distinct}`}
                valueStyle={{ color: result.a_udc_only ? '#d46b08' : undefined }}
              />
            </Col>
            <Col xs={12} md={4}>
              <Statistic
                title="Solo in C (righe / distinti)"
                value={`${result.c_cod_only} / ${result.c_cod_only_distinct}`}
                valueStyle={{ color: result.c_cod_only ? '#d46b08' : undefined }}
              />
            </Col>
          </Row>
          <Text type="secondary">
            A: foglio <Text code>{result.a_sheet}</Text>, {result.a_rows} righe con UDC COLLO. C: foglio{' '}
            <Text code>{result.c_sheet}</Text>, {result.c_rows} righe con Codice.
          </Text>
          {result.a_only_list.length > 0 && (
            <>
              <Divider orientation="left">UDC COLLO presenti in A ma non in C ({result.a_udc_only_distinct})</Divider>
              <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                {result.a_only_list.map((u) => <Tag key={u} color="orange" style={{ marginBottom: 4 }}>{u}</Tag>)}
                {result.a_udc_only_distinct > result.a_only_list.length && (
                  <Text type="secondary"> e altri {result.a_udc_only_distinct - result.a_only_list.length} (vedi file scaricato)</Text>
                )}
              </div>
            </>
          )}
          {result.c_only_list.length > 0 && (
            <>
              <Divider orientation="left">Codici presenti in C ma non in A ({result.c_cod_only_distinct})</Divider>
              <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                {result.c_only_list.map((u) => <Tag key={u} color="orange" style={{ marginBottom: 4 }}>{u}</Tag>)}
                {result.c_cod_only_distinct > result.c_only_list.length && (
                  <Text type="secondary"> e altri {result.c_cod_only_distinct - result.c_only_list.length} (vedi file scaricato)</Text>
                )}
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default ControlloUDC;
